import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Eye, UserPlus, ChevronLeft, ChevronRight,
  Calendar, X, ChevronsLeft, ChevronsRight, Loader2, RefreshCw,
  ChevronUp, ChevronDown, ArrowUpDown
} from 'lucide-react';
import DateRangePicker from '../components/PlayerProfile/DateRangePicker';
import PlayerEntriesModal from '../components/PlayerProfile/PlayerEntriesModal';
import { playerProfileService } from '../apis/playerProfileService';
import { Button } from '../components/ui/button';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from "react-redux";
import {
  SET_ALL_PLAYERS,
  SET_LOADING,
  SET_STATS_LOADING,
  SET_ERROR
} from "../Store/actions/playerprofile";

// Enhanced Loading Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };

  return (
    <Loader2 className={`animate-spin text-blue-600 ${sizes[size]} ${className}`} />
  );
};

// Table Row Skeleton Loader
const TableRowSkeleton = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full"></div>
        <div className="ml-4">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-16"></div>
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-6 bg-gray-200 rounded-full w-16"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="h-4 bg-gray-200 rounded w-20"></div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-right">
      <div className="flex items-center justify-end space-x-2">
        <div className="h-8 w-8 bg-gray-200 rounded"></div>
      </div>
    </td>
  </tr>
);

// Stats Card Skeleton
const StatsCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow p-4 border border-gray-200 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
    <div className="h-8 bg-gray-200 rounded w-16"></div>
  </div>
);

// Sortable Header Component
const SortableHeader = ({ children, sortKey, currentSort, onSort, className = "" }) => {
  const getSortIcon = () => {
    if (currentSort.key !== sortKey) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
    }

    return currentSort.direction === 'asc'
      ? <ChevronUp className="ml-1 h-4 w-4 text-blue-600" />
      : <ChevronDown className="ml-1 h-4 w-4 text-blue-600" />;
  };

  const handleClick = () => {
    let direction = 'asc';
    if (currentSort.key === sortKey && currentSort.direction === 'asc') {
      direction = 'desc';
    }
    onSort(sortKey, direction);
  };

  return (
    <th
      className={`px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <span>{children}</span>
        {getSortIcon()}
      </div>
    </th>
  );
};

const PlayerProfileModule = () => {
  const dispatch = useDispatch();
  const {
    allPlayers,
    loading: reduxLoading,
    statsLoading: reduxStatsLoading,
    error: reduxError
  } = useSelector((state) => state.playerprofile);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [dateRangeLabel, setDateRangeLabel] = useState('All Time');
  const [sortConfig, setSortConfig] = useState({ key: 'player_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPlayerImageModal, setShowPlayerImageModal] = useState(false);
  const [selectedPlayerImage, setSelectedPlayerImage] = useState('');
  const [selectedPlayerName, setSelectedPlayerName] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [stats, setStats] = useState(null);

  const [playerView, setPlayerView] = useState('all');
  const [activePlayers, setActivePlayers] = useState([]);
  const [activePlayersLoading, setActivePlayersLoading] = useState(false);

  const extractActivePlayers = (response) => {
    if (!response?.success) return [];
    return (
      response?.data?.data?.players ||
      response?.data?.players ||
      (Array.isArray(response?.data) ? response.data : [])
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: SET_LOADING, payload: true });
        dispatch({ type: SET_STATS_LOADING, payload: true });

        // Fetch all players and active players together so we can pin actives first
        const [playersResponse, activeResponse] = await Promise.all([
          playerProfileService.getDistinctPlayers(
            1,
            10000, // Large number to get all players
            ""
          ),
          playerProfileService.getActivePlayers().catch(() => null)
        ]);

        dispatch({ type: SET_ALL_PLAYERS, payload: playersResponse.data || [] });
        setActivePlayers(extractActivePlayers(activeResponse));

      } catch (error) {
        console.error('Error fetching data:', error);
        dispatch({ type: SET_ERROR, payload: 'Failed to fetch data' });
      } finally {
        dispatch({ type: SET_LOADING, payload: false });
        dispatch({ type: SET_STATS_LOADING, payload: false });
      }
    };

    fetchData();
  }, [dispatch]);

  // Fetch stats with loading state
  const fetchStats = useCallback(async () => {
    dispatch({ type: SET_STATS_LOADING, payload: true });
    try {
      const response = await playerProfileService.getProfileStats();
      if (response && response.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      dispatch({ type: SET_STATS_LOADING, payload: false });
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (searchTerm.trim() || dateRange) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, dateRange]);




  const activePlayerKeys = useMemo(() => {
    const keys = new Set();
    (activePlayers || []).forEach((player) => {
      if (player?.player_number) keys.add(`num:${player.player_number}`);
      if (player?.id != null) keys.add(`id:${player.id}`);
    });
    return keys;
  }, [activePlayers]);

  const isPlayerActive = useCallback((player) => {
    if (!player) return false;
    if (activePlayerKeys.size > 0) {
      return (
        (player.player_number && activePlayerKeys.has(`num:${player.player_number}`)) ||
        (player.id != null && activePlayerKeys.has(`id:${player.id}`))
      );
    }
    // Fallback if active_players list isn't available
    return Boolean(player.open_date) && !player.closed_date;
  }, [activePlayerKeys]);

  const compareBySortConfig = useCallback((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  }, [sortConfig]);

  const filteredAndSortedPlayers = useMemo(() => {
    const baseList = playerView === 'active'
      ? activePlayers
      : playerView === 'inactive'
        ? allPlayers.filter((player) => !isPlayerActive(player))
        : allPlayers;
    if (!baseList || baseList.length === 0) return [];

    let result = [...baseList];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(player =>
        (player.player_name?.toLowerCase().includes(term)) ||
        (player.email?.toLowerCase().includes(term)) ||
        (player.phone?.toLowerCase().includes(term)) ||
        (player.player_number?.toLowerCase().includes(term))
      );
    }

    // Apply date filter
    if (dateRange) {
      result = result.filter(player => {
        const openDate = new Date(player.open_date);
        const closeDate = new Date(player.closed_date);
        return (
          closeDate >= dateRange.startDate && openDate <= dateRange.endDate
        );
      });
    }

    return result.sort((a, b) => {
      // Always show currently active players first, then everyone else
      if (playerView === 'all') {
        const aActive = isPlayerActive(a);
        const bActive = isPlayerActive(b);
        if (aActive !== bActive) return aActive ? -1 : 1;
      }
      return compareBySortConfig(a, b);
    });
  }, [allPlayers, playerView, activePlayers, searchTerm, dateRange, isPlayerActive, compareBySortConfig]);

  // Paginate results
  const paginatedPlayers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedPlayers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedPlayers, currentPage, itemsPerPage]);

  const totalPlayers = filteredAndSortedPlayers.length;
  const totalPages = Math.ceil(totalPlayers / itemsPerPage);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  // Get player status badge
  const getStatusBadge = (player) => {
    return isPlayerActive(player) ? (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
        Closed
      </span>
    );
  };

  // Get player initials for avatar
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Handle viewing player details
  const handleViewPlayer = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  // Handle date range selection
  const handleDateRangeApply = (start, end, label) => {
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(end);
    endDate.setHours(23, 59, 59, 999);

    setDateRange({ startDate, endDate });
    setDateRangeLabel(label);
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const clearDateFilter = () => {
    setDateRange(null);
    setDateRangeLabel('All Time');
    setCurrentPage(1);
  };

  const handleToggleActivePlayersView = async () => {
    if (playerView === 'active') {
      setPlayerView('all');
      setCurrentPage(1);
      return;
    }

    setActivePlayersLoading(true);
    try {
      const response = await playerProfileService.getActivePlayers();
      if (!response?.success) {
        toast.error(response?.error || 'Failed to load active players');
        return;
      }

      const players = extractActivePlayers(response);
      setActivePlayers(players);
      setPlayerView('active');
      setCurrentPage(1);
      toast.success(`Loaded ${players.length} active player(s)`);
    } catch (error) {
      console.error('Error loading active players:', error);
      toast.error('Failed to load active players');
    } finally {
      setActivePlayersLoading(false);
    }
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Pagination calculations
  const getDisplayRange = () => {
    if (totalPlayers === 0) return { start: 0, end: 0 };
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalPlayers);
    return { start, end };
  };

  const { start: displayStart, end: displayEnd } = getDisplayRange();

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Refresh data
  const handleRefresh = () => {
    setSearchTerm('');
    setDateRange(null);
    setDateRangeLabel('All Time');
    setSortConfig({ key: 'player_name', direction: 'asc' });
    setCurrentPage(1);

    // Re-fetch data
    dispatch({ type: SET_LOADING, payload: true });
    Promise.all([
      playerProfileService.getDistinctPlayers(1, 10000, ""),
      playerProfileService.getActivePlayers().catch(() => null)
    ])
      .then(([response, activeResponse]) => {
        dispatch({ type: SET_ALL_PLAYERS, payload: response.data || [] });
        setActivePlayers(extractActivePlayers(activeResponse));
      })
      .catch(error => {
        console.error('Refresh failed:', error);
        dispatch({ type: SET_ERROR, payload: 'Refresh failed' });
      })
      .finally(() => {
        dispatch({ type: SET_LOADING, payload: false });
      });
  };


  // Render pagination buttons
  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPlayers, startPage + maxVisiblePages - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add ellipsis and first page if needed
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="h-8 w-8 border rounded text-sm font-medium transition-colors bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
        >
          1
        </button>
      );

      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className="px-2 py-1 text-gray-500">...</span>
        );
      }
    }

    // Add visible page buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`h-8 w-8 border rounded text-sm font-medium transition-colors ${currentPage === i
            ? 'bg-blue-50 border-blue-500 text-blue-600'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
            }`}
        >
          {i}
        </button>
      );
    }

    // Add ellipsis and last page if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" className="px-2 py-1 text-gray-500">...</span>
        );
      }

      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="h-8 w-8 border rounded text-sm font-medium transition-colors bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    return buttons;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Player Profiles</h1>
              <p className="text-gray-600 mt-2 text-sm">
                Manage and view all player profiles
              </p>
            </div>
    
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 justify-center text-center">
          {reduxStatsLoading ? (
            Array(4).fill(0).map((_, index) => (
              <StatsCardSkeleton key={index} />
            ))
          ) : stats ? (
            <>
              <div className="bg-white rounded-lg shadow p-4 border border-blue-200">
                <div className="text-sm font-medium text-blue-600">Total Players</div>
                <div className="text-2xl font-bold text-blue-800">{stats?.total_profiles || 0}</div>
              </div>
              <button
                type="button"
                onClick={handleToggleActivePlayersView}
                disabled={activePlayersLoading}
                className={`bg-white rounded-lg shadow p-4 border text-center transition-colors ${
                  playerView === 'active'
                    ? 'border-green-500 bg-green-50'
                    : 'border-green-200 hover:bg-green-50'
                } ${activePlayersLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                title={playerView === 'active' ? 'Click to show all players' : 'Click to view active players'}
              >
                <div className="text-sm font-medium text-green-600">
                  Active Players {playerView === 'active' ? '(showing)' : '(view)'}
                </div>
                <div className="text-2xl font-bold text-green-800">{stats?.active_profiles || activePlayers.length || 0}</div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlayerView(playerView === 'inactive' ? 'all' : 'inactive');
                  setCurrentPage(1);
                }}
                className={`bg-white rounded-lg shadow p-4 border text-center transition-colors cursor-pointer ${
                  playerView === 'inactive'
                    ? 'border-gray-500 bg-gray-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                title={playerView === 'inactive' ? 'Click to show all players' : 'Click to view inactive players'}
              >
                <div className="text-sm font-medium text-gray-600">
                  Inactive Players {playerView === 'inactive' ? '(showing)' : '(view)'}
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {Math.max((stats?.total_profiles || allPlayers.length || 0) - (stats?.active_profiles || activePlayers.length || 0), 0)}
                </div>
              </button>
              <div className="bg-white rounded-lg shadow p-4 border border-purple-200">
                <div className="text-sm font-medium text-purple-600">Avg Session Time</div>
                <div className="text-2xl font-bold text-purple-800">{Math.round(stats?.avg_session_mins || 0)} min</div>
              </div>
            </>
          ) : (
            <div className="col-span-full text-center py-4 text-gray-500">
              Unable to load statistics
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search players..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {playerView !== 'all' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    playerView === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    Showing {playerView} players
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPlayerView('all');
                      setCurrentPage(1);
                    }}
                    className="text-xs font-medium text-green-700 hover:text-green-900 underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowDatePicker(true)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Calendar className="mr-2" size={16} />
                {dateRange ? (
                  <span>
                    {formatDateDisplay(dateRange.startDate)} - {formatDateDisplay(dateRange.endDate)}
                  </span>
                ) : (
                  <span>{dateRangeLabel}</span>
                )}
              </button>

              {dateRange && (
                <button
                  onClick={clearDateFilter}
                  className="flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  <X size={16} />
                  <span className="ml-1">Clear</span>
                </button>
              )}

              <button
                onClick={handleRefresh}
                disabled={reduxLoading}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {reduxLoading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <RefreshCw className="mr-2" size={16} />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {reduxError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{reduxError}</div>
                <div className="mt-4">
                  <button
                    onClick={handleRefresh}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Players Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {reduxLoading ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Player
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Game & Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member Since
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array(itemsPerPage).fill(0).map((_, index) => (
                    <TableRowSkeleton key={index} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : paginatedPlayers.length === 0 && !isFiltering ? (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No players found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding a new player'}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    clearDateFilter();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <UserPlus className="mr-2" size={16} />
                  Add Player
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader
                        sortKey="player_name"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Player
                      </SortableHeader>
                      <SortableHeader
                        sortKey="email"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Contact
                      </SortableHeader>
                      <SortableHeader
                        sortKey="game_name"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Game & Location
                      </SortableHeader>
                      <SortableHeader
                        sortKey="closed_date"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Status
                      </SortableHeader>
                      <SortableHeader
                        sortKey="member_since"
                        currentSort={sortConfig}
                        onSort={(key, direction) => {
                          setSortConfig({ key, direction });
                          setCurrentPage(1);
                        }}
                      >
                        Member Since
                      </SortableHeader>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedPlayers
                      .filter(
                        (player) => {
                          // Filter out invalid player names
                          if (!player.player_name) return false;
                          const trimmedName = player.player_name.trim();
                          // Filter out entries that are just commas, empty, or invalid
                          if (!trimmedName || trimmedName === ',' || trimmedName.length < 2) return false;
                          return true;
                        }
                      )
                      // Remove duplicates based on player_number and player_name combination
                      .filter((player, index, array) => {
                        // Create a unique key for each player
                        const playerKey = `${player.player_number || 'no-number'}-${player.player_name?.trim() || 'no-name'}`;
                        
                        // Find the first occurrence of this player
                        const firstIndex = array.findIndex(p => {
                          const pKey = `${p.player_number || 'no-number'}-${p.player_name?.trim() || 'no-name'}`;
                          return pKey === playerKey;
                        });

                        // Only keep if this is the first occurrence
                        return firstIndex === index;
                      })
                      .map((player) => (
                        <tr
                          key={`${player.id}-${player.player_number || 'no-number'}-${player.player_name}`} // More unique key
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleViewPlayer(player)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                {player.image_url ? (
                                  <img
                                    src={player.image_url}
                                    alt={player.player_name}
                                    className="h-full w-full object-contain rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedPlayerImage(player.image_url);
                                      setSelectedPlayerName(player.player_name);
                                      setShowPlayerImageModal(true);
                                    }}
                                  />
                                ) : (
                                  <span className="text-sm font-medium text-blue-600">
                                    {getInitials(player.player_name)}
                                  </span>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-blue-700">{player.player_name || 'N/A'}</div>
                                <div className="text-sm text-gray-500">{player.player_number || 'N/A'}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{player.email || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{player.phone || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{player.game_name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{player.location || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(player)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(player?.member_since)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPlayer(player);
                                }}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50 transition-colors"
                                title="View Details"
                              >
                                <Eye size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{displayStart}</span> to{" "}
                        <span className="font-medium">{displayEnd}</span> of{" "}
                        <span className="font-medium">{totalPlayers}</span> results
                      </p>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={5}>5 per page</option>
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                        <option value={500}>500 per page</option>
                        <option value={1000}>1000 per page</option>
                        <option value={2000}>2000 per page</option>
                        <option value={3000}>3000 per page</option>
                      </select>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="First Page"
                        >
                          <span className="sr-only">First</span>
                          <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Previous Page"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                        </button>

                        <div className="flex items-center space-x-1 px-2">
                          {renderPaginationButtons()}
                        </div>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Next Page"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Last Page"
                        >
                          <span className="sr-only">Last</span>
                          <ChevronsRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Player Details Modal */}
      {selectedPlayer && (
        <PlayerEntriesModal
          player={selectedPlayer}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlayer(null);
          }}
        />
      )}

      {/* Date Range Picker Modal */}
      {showDatePicker && (
        <DateRangePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange?.startDate}
          initialEndDate={dateRange?.endDate}
        />
      )}

      {showPlayerImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{selectedPlayerName}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPlayerImageModal(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <img
                src={selectedPlayerImage}
                alt={selectedPlayerName}
                className="w-full h-auto max-h-[70vh] object-contain rounded"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerProfileModule;